import { useState, useCallback, type ReactNode } from 'react';
import { Calendar, ClipboardList, Container, Truck } from 'lucide-react';
import { useUserProfile } from '../context/UserProfileContext';
import { DispatchContent } from './DispatchPage';
import { FleetContent } from './FleetPage';
import { ScheduleContent } from './SchedulePage';
import { MoverJobsPage } from './MoverJobsPage';

type OperationsTab = 'dispatch' | 'fleet' | 'schedule' | 'jobs';
const TAB_KEY = 'iomi_operations_tab';

const TAB_CONFIG: { id: OperationsTab; label: string; icon: typeof Truck }[] = [
  { id: 'dispatch', label: 'Dispatch', icon: ClipboardList },
  { id: 'schedule', label: 'Schedule', icon: Calendar },
  { id: 'fleet', label: 'Fleet', icon: Container },
  { id: 'jobs', label: 'Jobs', icon: Truck },
];

export function OperationsPage() {
  const { activeOrg, currentUser, loading } = useUserProfile();
  const orgId = activeOrg?.orgId ?? null;

  const dispatchEnabled = activeOrg?.featureDispatch !== false;
  const fleetEnabled = activeOrg?.featureFleetManagement ?? false;
  const jobsEnabled = activeOrg?.featureJobs !== false;
  const isMover = activeOrg?.role === 'mover' && !currentUser?.isPlatformAdmin;

  const showDispatchTab = dispatchEnabled && !isMover;
  const showScheduleTab = dispatchEnabled && !isMover;
  const showFleetTab = fleetEnabled;
  const showJobsTab = jobsEnabled;

  const tabVisibility: Record<OperationsTab, boolean> = {
    dispatch: showDispatchTab,
    schedule: showScheduleTab,
    fleet: showFleetTab,
    jobs: showJobsTab,
  };

  const visibleTabConfigs = TAB_CONFIG.filter(t => tabVisibility[t.id]);
  const showTabs = visibleTabConfigs.length >= 2;

  const defaultTab: OperationsTab = showDispatchTab ? 'dispatch' : showJobsTab ? 'jobs' : showFleetTab ? 'fleet' : 'schedule';

  const [activeTab, setActiveTab] = useState<OperationsTab>(() => {
    if (!showTabs) return defaultTab;
    const stored = localStorage.getItem(TAB_KEY);
    if (stored && tabVisibility[stored as OperationsTab]) return stored as OperationsTab;
    return defaultTab;
  });

  // Sidebar content provided by each tab's content component
  const [sidebarContent, setSidebarContent] = useState<ReactNode>(null);
  const handleSidebarContent = useCallback((content: ReactNode) => {
    setSidebarContent(content);
  }, []);

  function switchTab(tab: OperationsTab) {
    setActiveTab(tab);
    setSidebarContent(null); // Clear sidebar when switching tabs
    localStorage.setItem(TAB_KEY, tab);
  }

  if (loading) return null;
  if (!showDispatchTab && !showFleetTab && !showScheduleTab && !showJobsTab) {
    return (
      <div className="px-4 pt-16 flex flex-col items-center text-center gap-3">
        <p className="text-small text-slate font-semibold">Operations is not enabled for this organization.</p>
      </div>
    );
  }
  if (!orgId) return null;

  const tabContent = (
    <>
      {activeTab === 'dispatch' && showDispatchTab && (
        <DispatchContent orgId={orgId} onSidebarContent={handleSidebarContent} />
      )}
      {activeTab === 'schedule' && showScheduleTab && (
        <ScheduleContent orgId={orgId} onSidebarContent={handleSidebarContent} />
      )}
      {activeTab === 'fleet' && showFleetTab && (
        <FleetContent orgId={orgId} onSidebarContent={handleSidebarContent} embedded />
      )}
      {activeTab === 'jobs' && showJobsTab && (
        <MoverJobsPage embedded onSidebarContent={handleSidebarContent} />
      )}
    </>
  );

  return (
    <>
      {/* ── Mobile layout ──────────────────────────────────────────── */}
      <div className="md:hidden px-4 pt-4 pb-24">
        <div className="flex items-center gap-4 mb-4">
          <h1 className="font-display text-h1 text-midnight shrink-0">Operations</h1>
        </div>

        {showTabs && (
          <div className="flex items-center gap-1 p-1 bg-mist rounded-lg mb-4">
            {visibleTabConfigs.map(t => (
              <button
                key={t.id}
                type="button"
                onClick={() => switchTab(t.id)}
                className={`flex-1 flex items-center justify-center gap-1.5 h-9 rounded-md text-[12px] font-semibold transition-colors duration-150 ${
                  activeTab === t.id ? 'bg-white text-midnight shadow-sm' : 'text-slate hover:text-midnight'
                }`}
              >
                <t.icon size={13} />
                {t.label}
              </button>
            ))}
          </div>
        )}

        {tabContent}
      </div>

      {/* ── Desktop layout (md+) ───────────────────────────────────── */}
      <div className="hidden md:block px-6 pt-4 pb-24 max-w-6xl mx-auto">
        <div className="mb-4">
          <h1 className="font-display text-h1 text-midnight">Operations</h1>
        </div>

        <div className="flex gap-6 items-start">
          {/* Left sidebar: vertical tabs + tab-specific stats */}
          {showTabs && (
            <aside className="w-52 shrink-0 sticky top-20">
              <nav className="flex flex-col gap-1">
                {visibleTabConfigs.map(t => {
                  const isActive = activeTab === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => switchTab(t.id)}
                      className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-[13px] font-semibold text-left transition-all duration-150 ${
                        isActive
                          ? 'bg-plum text-white shadow-brand-sm'
                          : 'text-slate hover:bg-mist hover:text-midnight'
                      }`}
                    >
                      <t.icon size={16} />
                      {t.label}
                    </button>
                  );
                })}
              </nav>

              {/* Tab-specific sidebar content (stats, toggles, etc.) */}
              {sidebarContent && (
                <>
                  <div className="my-4 border-t border-stone" />
                  {sidebarContent}
                </>
              )}
            </aside>
          )}

          {/* Right content area */}
          <div className="flex-1 min-w-0">
            {tabContent}
          </div>
        </div>
      </div>
    </>
  );
}
